/**
 * Sidebar — navegação à esquerda (LDS) com ícones para as áreas:
 * dashboards, guardrails, contas e logs.
 * Suporta modo colapsado (apenas ícones) com botão de toggle.
 *
 * Requisitos: 14.3 (sidebar com ícones para as áreas).
 */

import type { ComponentType } from 'react';
import {
  LdsIconPower,
  LdsIconDashboard,
  LdsIconBuildingBank,
  LdsIconFileText,
  LdsIconChevronLeft,
  LdsIconChevronRight,
} from '@lds/react-icons';
import { LdsTypography } from '@lds/react';
import styles from './Sidebar.module.scss';

export const DRAWER_WIDTH = 240;
export const DRAWER_WIDTH_COLLAPSED = 64;

interface NavItem {
  label: string;
  Icon: ComponentType<{ size?: string }>;
  path: string;
}

const navItems: NavItem[] = [
  { label: 'Dashboards', Icon: LdsIconDashboard, path: '/dashboards' },
  { label: 'Guardrails', Icon: LdsIconPower, path: '/guardrails' },
  { label: 'Contas', Icon: LdsIconBuildingBank, path: '/accounts' },
  { label: 'Logs', Icon: LdsIconFileText, path: '/logs' },
];

export interface SidebarProps {
  /** Rota ativa (para destaque). */
  activePath?: string;
  /** Handler de navegação. */
  onNavigate?: (path: string) => void;
  /** Sidebar colapsada (apenas ícones). */
  collapsed?: boolean;
  /** Callback ao alternar entre expandido/colapsado. */
  onToggleCollapse?: () => void;
}

export function Sidebar({ activePath, onNavigate, collapsed = false, onToggleCollapse }: SidebarProps) {
  return (
    <nav
      className={`${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : ''}`}
      aria-label="Navegação principal"
    >
      <ul className={styles.list}>
        {navItems.map(({ label, Icon, path }) => {
          const active = activePath === path;
          return (
            <li key={path}>
              <button
                type="button"
                className={`${styles.item} ${active ? styles['item--active'] : ''}`}
                aria-label={label}
                aria-current={active ? 'page' : undefined}
                title={collapsed ? label : undefined}
                onClick={() => onNavigate?.(path)}
              >
                <span className={styles.icon}>
                  <Icon size="24" />
                </span>
                {!collapsed && <LdsTypography variant="bodyMd">{label}</LdsTypography>}
              </button>
            </li>
          );
        })}
      </ul>

      {/* Toggle button */}
      <button
        type="button"
        className={styles.collapseToggle}
        onClick={onToggleCollapse}
        aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
        title={collapsed ? 'Expandir menu' : 'Recolher menu'}
      >
        {collapsed ? <LdsIconChevronRight size="18" /> : <LdsIconChevronLeft size="18" />}
      </button>
    </nav>
  );
}
